import { useState } from 'react';
import { Box, Typography, Link, useMediaQuery, useTheme } from '@mui/material';
import { LoginForm } from '@components/LoginForm';
import { ForgotPassDialog } from '@components/dialogs/ForgotPassDialog';
import { REGISTER_URL } from '@consts/urls';
import { colors, typography as typographyTokens, radii, shadows } from '@components/design-system/tokens';

export const Login = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [showForgotPass, setShowForgotPass] = useState(false);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
            }}>
            {/* Left Panel - Branding */}
            <Box
                sx={{
                    flex: isMobile ? 'none' : 1,
                    background: `linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[800]} 100%)`,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: isMobile ? 4 : 6,
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: isMobile ? 150 : 'auto',
                }}>
                {/* Decorative circles */}
                <Box
                    sx={{
                        position: 'absolute',
                        width: 400,
                        height: 400,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        top: -100,
                        left: -100,
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        width: 300,
                        height: 300,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.03)',
                        bottom: -50,
                        right: -50,
                    }}
                />

                {/* Logo and tagline */}
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        textAlign: 'center',
                    }}>
                    <Box
                        component='img'
                        src='/logo.png'
                        alt='Mobile Outfitters'
                        sx={{
                            height: isMobile ? 48 : 64,
                            filter: 'brightness(0) invert(1)',
                            mb: 3,
                        }}
                    />
                    {!isMobile && (
                        <>
                            <Typography
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.95)',
                                    fontSize: typographyTokens.fontSize['2xl'],
                                    fontWeight: typographyTokens.fontWeight.semibold,
                                    fontFamily: typographyTokens.fontFamily.display,
                                    mb: 1.5,
                                }}>
                                MO Insights
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontSize: typographyTokens.fontSize.md,
                                    maxWidth: 320,
                                    lineHeight: 1.6,
                                }}>
                                Powerful analytics and management tools for your business
                            </Typography>
                        </>
                    )}
                </Box>
            </Box>

            {/* Right Panel - Login Form */}
            <Box
                sx={{
                    flex: isMobile ? 'none' : '0 0 480px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: isMobile ? 'flex-start' : 'center',
                    alignItems: 'center',
                    p: isMobile ? 3 : 6,
                    pb: isMobile ? 6 : 6,
                    backgroundColor: colors.neutral[0],
                }}>
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 400,
                    }}>
                    {/* Welcome text */}
                    <Box sx={{ mb: 4, textAlign: isMobile ? 'center' : 'left' }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize['2xl'],
                                fontWeight: typographyTokens.fontWeight.bold,
                                color: colors.neutral[900],
                                mb: 1,
                            }}>
                            Welcome back
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.md,
                                color: colors.neutral[500],
                            }}>
                            Sign in to your account to continue
                        </Typography>
                    </Box>

                    {/* Login Form */}
                    <LoginForm />

                    {/* Links */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 2,
                            mt: 4,
                            pt: 3,
                            borderTop: `1px solid ${colors.neutral[200]}`,
                        }}>
                        <Typography
                            component='button'
                            onClick={() => setShowForgotPass(true)}
                            sx={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                fontSize: typographyTokens.fontSize.sm,
                                color: colors.neutral[500],
                                cursor: 'pointer',
                                transition: 'color 150ms ease',
                                '&:hover': {
                                    color: colors.primary[600],
                                },
                            }}>
                            Forgot password?
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.sm,
                                color: colors.neutral[300],
                            }}>
                            •
                        </Typography>

                        <Link
                            href={REGISTER_URL}
                            target='_blank'
                            underline='none'
                            sx={{
                                fontSize: typographyTokens.fontSize.sm,
                                color: colors.neutral[500],
                                transition: 'color 150ms ease',
                                '&:hover': {
                                    color: colors.primary[600],
                                },
                            }}>
                            Create account
                        </Link>
                    </Box>
                </Box>
            </Box>

            <ForgotPassDialog open={showForgotPass} onClose={() => setShowForgotPass(false)} />
        </Box>
    );
};
